import {
  Box,
  Button,
  Center,
  FormControl,
  FormErrorMessage,
  HStack,
  Input,
  InputGroup,
  InputLeftElement,
  Menu,
  MenuButton,
  MenuItem,
  MenuList,
} from '@chakra-ui/react';
import { FormEvent, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useSearchParams } from 'react-router-dom';
import { EmptyView } from '~components/EmptyView';
import { ChevronDownIcon, SearchIcon } from '~components/ui/icons';
import { PageContent } from '~components/ui/PageContent';
import { PageHeading } from '~components/ui/PageHeading';
import { CustomerSearchResults } from './CustomerSearchResults';
import { isMacAddressOrSerialNumber } from './deviceIdentifiers';
import { DeviceSearchResults } from './DeviceSearchResults';
import { OrganizationSearchResults } from './OrganizationSearchResults';
import { SEARCH_PAGE_SIZES, SearchPageSizeContext } from './SearchResultsTable';
import { searchPlaceholderKeys, SearchType, searchTypeLabelKeys, searchTypes } from './searchTypes';
import { UserSearchResults } from './UserSearchResults';

const resultsBySearchType = {
  [SearchType.User]: UserSearchResults,
  [SearchType.Organization]: OrganizationSearchResults,
  [SearchType.Customer]: CustomerSearchResults,
  [SearchType.Device]: DeviceSearchResults,
};

const SEARCH_CONTROL_HEIGHT = '12';

function parseSearchType(value: string | null): SearchType | null {
  return searchTypes.find((type) => type === value) ?? null;
}

export function AdminSearch() {
  const { t } = useTranslation();
  // The submitted search lives in the URL, so it survives a refresh and the back button.
  const [searchParams, setSearchParams] = useSearchParams();
  const submittedType = parseSearchType(searchParams.get('type'));
  const submittedTerm = searchParams.get('q') ?? '';

  const [type, setType] = useState<SearchType | null>(submittedType);
  // Kept here, not in each result table, so it survives new searches (which remount the table).
  const [pageSize, setPageSize] = useState(SEARCH_PAGE_SIZES[0]);
  const [term, setTerm] = useState(submittedTerm);

  const trimmedTerm = term.trim();
  // Devices are only ever matched exactly, so only search once the input is a full MAC address
  // or serial number.
  const isInvalidDeviceTerm =
    type === SearchType.Device &&
    trimmedTerm.length > 0 &&
    !isMacAddressOrSerialNumber(trimmedTerm);
  const canSearch = type !== null && trimmedTerm.length > 0 && !isInvalidDeviceTerm;

  // A new type means a new kind of search: drop the previous results and term, back to the
  // "Search anything" state.
  const handleTypeChange = (value: SearchType) => {
    setType(value);
    setTerm('');
    setSearchParams({});
  };

  const handleSubmit = (event: FormEvent) => {
    event.preventDefault();

    if (canSearch) {
      setSearchParams({ type, q: trimmedTerm });
    }
  };

  const Results = submittedType && submittedTerm ? resultsBySearchType[submittedType] : null;

  return (
    <SearchPageSizeContext.Provider value={{ pageSize, setPageSize }}>
      <PageContent paddingTop="2">
        <HStack color="gray.500" fontSize="sm" spacing="1" marginBottom="1">
          <SearchIcon width="4" height="4" />
          <Box>{t('adminSearch')}</Box>
        </HStack>
        <PageHeading paddingTop="1" marginBottom="0">
          {t('search')}
        </PageHeading>

        <HStack
          as="form"
          onSubmit={handleSubmit}
          spacing="5"
          marginBottom="6"
          marginTop="8"
          alignItems="flex-start"
        >
          <Menu>
            <MenuButton
              as={Button}
              variant="outline"
              colorScheme="gray"
              bgColor="white"
              rightIcon={<ChevronDownIcon />}
              minWidth="36"
              height={SEARCH_CONTROL_HEIGHT}
              flexShrink={0}
            >
              {type ? t(searchTypeLabelKeys[type]) : t('selectType')}
            </MenuButton>
            <MenuList>
              {searchTypes.map((value) => (
                <MenuItem key={value} onClick={() => handleTypeChange(value)}>
                  {t(searchTypeLabelKeys[value])}
                </MenuItem>
              ))}
            </MenuList>
          </Menu>
          <FormControl isInvalid={isInvalidDeviceTerm}>
            <InputGroup>
              <InputLeftElement pointerEvents="none" height={SEARCH_CONTROL_HEIGHT}>
                <SearchIcon color="gray.500" />
              </InputLeftElement>
              <Input
                height={SEARCH_CONTROL_HEIGHT}
                bgColor="white"
                value={term}
                onChange={(event) => setTerm(event.target.value)}
                placeholder={type ? t(searchPlaceholderKeys[type]) : t('chooseTypeOfSearch')}
                isDisabled={type === null}
              />
            </InputGroup>
            <FormErrorMessage>{t('adminSearchInvalidDeviceTerm')}</FormErrorMessage>
          </FormControl>
          <Button
            type="submit"
            colorScheme="blue"
            isDisabled={!canSearch}
            height={SEARCH_CONTROL_HEIGHT}
            flexShrink={0}
          >
            {t('search')}
          </Button>
        </HStack>

        {Results ? (
          // Keyed so that each new search starts back on the first page (keeping the page size).
          <Results key={`${submittedType}:${submittedTerm}`} searchTerm={submittedTerm} />
        ) : (
          <EmptyView
            icon={
              <Center width="48" height="48" borderRadius="full" bgColor="blue.50">
                <SearchIcon width="20" height="20" color="blue.700" />
              </Center>
            }
            title={t('searchAnything')}
            description={t('searchAnythingDescription')}
          />
        )}
      </PageContent>
    </SearchPageSizeContext.Provider>
  );
}
